import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { useTranslation } from '@front10/utils';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Box from '../../elements/Box';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Button from '../../elements/Button';
import Text from '../../elements/Text';
import Icon from '../../elements/Icon';
import Disclaimer from '../../others/Disclaimer';
// import Alert from '../../others/Alert';
import SkeletonFlightRecommender from '../../others/Skeleton/SkeletonFlightRecommender';
import SkeletonFlightRecommenderMobile from '../../others/Skeleton/SkeletonFlightRecommenderMobile';
import LoadMorePaginator from '../../pagination/LoadMorePaginator';

import FareListTwoColumns from '../../lists/FareListTwoColumns';
import FareItemMediumSizeGrouped from '../../items/FareItemMediumSizeGrouped';
import Loading from './atoms/ChecklistLoader';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import Autocomplete from '../../controls/Autocomplete';
import Sorting from '../../controls/SecondaryFilters/Sorting';

import { useListItemFocus, useListFocusHandlers } from '../_commons/list';
import { useFetchCredits } from './hooks/useCredits';

//-------------------------------------
// EveryMundo integration
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/faresSmart';
import { getRecommendations } from '../../../_integrations/everymundo/recommendations';
import { getAirports } from '../../../_integrations/everymundo/airports';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters } from './hooks/useFilters';

import HeaderContainer from '../../elements/HeaderContainer';

function FlightsRecommender(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    direction,
    getAirports: getAirportsFn,
    getFares: getFaresFn,
    getRecommendations: getRecommendationsFn,
    showDisclaimer,
    disclaimerContentSettings,
    mainContentSettings,
    theme,
    disclaimer,
    enableSEO = true,
    isIframe,
    originDestinationLogic,
    showLikeIcons = true,
    isMobile = false,
    showCTA = true,
    debug = false,
    skeletonSettings,
    numberOfAttempts = 3
  } = props;

  const translate = useTranslation();
  // INFO: This ref is used for show animation when user fetch any query
  const statusTextRef = useRef(null);
  const loadMoreRef = useRef(null);

  const { credits, loading, canMakeRequest, fetchCredits } = useFetchCredits(integrationSettings);

  const [className, style] = useStyles(props, { direction, theme });

  const { focusIndex, setFocusIndex, ...focusHandlers } = useListFocusHandlers();
  const [loadMoreClickAction, setLoadMoreClickAction] = React.useState(false);

  const suggestionsOptions = React.useMemo(() => {
    const elementsToShow = props?.components?.prompt?.elementsToShow;
    let options = props?.components?.prompt?.options;
    if (
      Number.isInteger(elementsToShow) &&
      elementsToShow >= 0 &&
      Array.isArray(options) &&
      options?.length > elementsToShow
    ) {
      options = options.slice(0, elementsToShow);
    }
    return options;
  }, [props?.components?.prompt?.elementsToShow, props?.components?.prompt?.options]);

  const {
    fares: { data, dataPrompt, match, retry, showSecondQuery, sputnikArports, ...fares },
    prompt,
    disabledCTA,
    dispatch,
    searchRecommendation,
    isError,
    isPromptInvalid,
    sorting,
    loadMoreClick,
    likeActions
  } = useFilters({
    promptSuggestion: suggestionsOptions ? suggestionsOptions : [],
    originDestinationLogic,
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports: getAirportsFn },
    pagination: props?.components?.pagination,
    getRecommendations: getRecommendationsFn,
    getFares: getFaresFn,
    credits,
    fetchCredits,
    translate,
    ...focusHandlers
  });

  const [nextFocusIndex, listItemRef] = useListItemFocus({
    data,
    focusIndex,
    setFocusIndex
  });

  const isFetchingLLM = match('LOADING');
  const isFetchingFares = match('FETCH_FARES');
  const isComplete = match('COMPLETE');
  const noFaresToShow = match('NO_RESULTS');
  const initialRender = match('INIT');

  React.useEffect(() => {
    if (loadMoreClickAction) {
      setLoadMoreClickAction(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isFetchingLoadMoreFares = loadMoreClickAction && isFetchingFares;

  useEffect(() => {
    if (isFetchingFares && statusTextRef.current && !showSecondQuery && !loadMoreClick) {
      const aText = dataPrompt?.explanation || '';
      const iSpeed = 40; // INFO: Velocity of the writer in ms
      let iTextPos = 0;
      const destination = statusTextRef.current;

      function type() {
        if (iTextPos < aText.length) {
          destination.innerHTML = aText.substring(0, iTextPos + 1) + '|';
          iTextPos++;
          setTimeout(type, iSpeed);
        } else {
          destination.innerHTML = aText; //Requirement: Remove the cursor at end of the line
        }
      }

      type();
    }
  }, [isFetchingFares, statusTextRef, dataPrompt, showSecondQuery, loadMoreClick]);

  // Disabling the scroll on the product page to the calculation that VG makes for the height of the Iframe
  if (isIframe && !isSSR()) {
    document.body.style.overflow = 'hidden';
  }

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  const handleClick = (fare, callToActionLabel) => {
    dispatch({ type: 'FARE_CLICK', payload: { fare, callToActionLabel } });
  };

  function isUnifiedCityDescriptionRequired(dataPrompt, sputnikArports) {
    if (Object.keys(dataPrompt).length > 0) {
      if (sputnikArports === null || !Array.isArray(sputnikArports) || !dataPrompt?.summary) {
        return false;
      }
      let expectedCity = null;
      for (const airport of sputnikArports) {
        const entry = dataPrompt?.summary[airport];
        if (!entry || !entry?.cityName) {
          return false;
        }
        if (expectedCity === null) {
          expectedCity = entry.cityName.toLowerCase().trim();
        } else if (expectedCity !== entry.cityName.toLowerCase().trim()) {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  const itemSettings = {
    ...props?.components?.fareList?.itemSettings,
    showLocationInfo: !showSecondQuery,
    callToActionOnClick: ({ data: fare }, callToActionLabel) => {
      handleClick(fare, callToActionLabel);
    },
    siteEdition: pageContext?.siteEdition,
    unifiedCityDescriptionRequired:
      !showSecondQuery && isUnifiedCityDescriptionRequired(dataPrompt, sputnikArports),
    dispatch
  };

  const titleLabelKey = () => {
    if (isError) {
      return 'cmp-flight-recommendation-error-title-labelKey';
    }
    if (isPromptInvalid) {
      return 'cmp-flights-recommender-prompt-invalid-info-error';
    }
    return props?.title?.content || props?.titleKey;
  };

  const statusTextDescriptionValue = () => {
    if (isError) {
      return translate(
        'cmp-flight-recommendation-error-text-status',
        'Our system is currently handling a lot of requests. Please try again later.'
      );
    }
    if (isPromptInvalid) {
      return translate(
        'cmp-flight-recommendation-prompt-invalid-text-status',
        'Include destinations, travel dates and other details for me to help you find flights.'
      );
    }

    return '';
  };

  const likeUnlikeIcons = () => {
    if (showLikeIcons && !isError && (isFetchingFares || isComplete)) {
      return (
        <Box display="flex" marginLeft="auto" {...props?.components?.thumbs}>
          <Button
            color="transparent"
            display="flex"
            alignItems="center"
            cmp-styles={{ height: '20px' }}
            ariaLabel={translate('cmp-flights-recommendation-like-prompt', 'Like prompt')}
            {...props?.components?.thumbs?.thumbUp}
            {...likeActions?.like}
          >
            <Icon size={20} icon="thumb-up" />
          </Button>
          <Button
            color="transparent"
            display="flex"
            alignItems="center"
            cmp-styles={{ height: '20px' }}
            ariaLabel={translate('cmp-flights-recommendation-unlike-prompt', 'Unlike prompt')}
            {...props?.components?.thumbs?.thumbDown}
            {...likeActions?.unlike}
          >
            <Icon size={20} icon="thumb-down" />
          </Button>
        </Box>
      );
    }
    return null;
  };

  const renderLoading = () => {
    return (
      <Row gutters={false}>
        <Col xs={12}>
          <Loading {...props?.components?.loading} isLoading={isFetchingLLM} />
        </Col>
      </Row>
    );
  };

  const renderCtaButton = ({ goBack }) => {
    return (
      <Box cmp-styles={{ display: 'flex', flex: '0.1' }} {...props?.components?.ctaColumn}>
        <Button
          {...props?.components?.cta}
          disabled={disabledCTA || !canMakeRequest}
          onClick={() => {
            if (goBack) {
              goBack();
            }
            searchRecommendation();
          }}
        >
          <Icon icon="sparkles" {...props?.components?.cta?.icon} />
          <Text {...props?.components?.cta?.text}>
            {translate('flight-recommender-cta', 'Search')}
          </Text>
        </Button>
      </Box>
    );
  };

  const renderSuggestionsBox = () => {
    return initialRender ? (
      <Row {...props?.components?.suggestionBox?.container}>
        {suggestionsOptions?.map((suggestionOption, index) => {
          const space = props?.components?.suggestionBox?.columns?.space;
          const spaceSettings = isMobile
            ? {}
            : {
                margin:
                  index === 0
                    ? `${space} ${space} ${space} 0px`
                    : index === suggestionsOptions.length - 1
                    ? `${space} 0px ${space} ${space}`
                    : space
              };
          return (
            <Col {...props?.components?.suggestionBox?.columns} {...spaceSettings}>
              <Box
                onClick={() => {
                  prompt?.onChange(translate(suggestionOption.label));
                }}
                {...props?.components?.suggestionBox}
              >
                <Text {...props?.components?.suggestionBox?.suggestionLabel}>
                  {translate(suggestionOption.label)}
                </Text>
              </Box>
            </Col>
          );
        })}
      </Row>
    ) : null;
  };

  const renderListTwoColumns = () => {
    const newData =
      !showSecondQuery && isUnifiedCityDescriptionRequired(dataPrompt, sputnikArports)
        ? [{ fares: data, info: data[0].info, faresCount: data.length }]
        : data;
    return (
      <FareListTwoColumns
        {...props?.components?.fareList}
        itemSettings={itemSettings}
        ItemComponentInstance={FareItemMediumSizeGrouped}
        itemsData={newData}
        focusOn={nextFocusIndex}
        focusReferences={(ref) => {
          listItemRef.current = ref;
        }}
      />
    );
  };

  const promptSettings = {
    ...props?.components.prompt,
    dropDownMaxHeight:
      (noFaresToShow || isPromptInvalid) && !isMobile
        ? 110
        : props?.components.prompt?.dropDownHeight || 320
  };

  if (loading && debug) {
    return <div>Fetching credits ...</div>; // Displays a loading message while waiting for the response
  }
  if (credits.done) {
    if (debug) {
      return (
        <div>{`The available credits for the component have already been used: ${credits?.value}`}</div>
      );
    } else {
      return null;
    }
  } else {
    return (
      <Box
        position="relative"
        className={`${className} ${FlightsRecommender.componentName} ${
          isFetchingLLM ? 'line-animation' : ''
        }`}
        style={style}
        dataTestId="flightsRecommender"
      >
        {isFetchingLLM ? (
          renderLoading()
        ) : (
          <>
            {debug && <pre>{JSON.stringify(credits, null, 8)}</pre>}
            <HeaderContainer
              defaultTitleValue={'LOWEST DAILY FARES FOR THE UPCOMING 90 DAYS'}
              {...props}
              titleKey={titleLabelKey()}
              showSubTitle={false}
            />
            {!isError && !isPromptInvalid && props?.showTitle && props?.showBetaBox && (
              <Box {...props?.betaLogoBox}>
                <Text {...props?.betaLogoText}>
                  {translate('cmp-flight-recommendation-beta-labelKey', 'Beta')}
                </Text>
              </Box>
            )}
            {!canMakeRequest && (
              <Row {...props?.components.maxUsedCredits?.container}>
                <Col {...props?.components.maxUsedCredits}>
                  <Text {...props?.components?.maxUsedCredits?.label}>
                    {translate(
                      'cmp-flights-recommendation-max-used-credits-label',
                      'You’ve reached the request limit for this session.'
                    )}
                  </Text>
                  <Text {...props?.components?.maxUsedCredits?.description}>
                    {translate(
                      'cmp-flights-recommendation-max-used-credits-description',
                      'Select from your previous search results, or try again in 30 minutes.'
                    )}
                  </Text>
                </Col>
              </Row>
            )}
            <Row {...mainContentSettings} style={{ position: 'relative', clear: 'both' }}>
              <Col xs={13} {...props?.components?.promptColumn}>
                <Autocomplete
                  {...promptSettings}
                  options={initialRender || match('ERROR') ? [] : suggestionsOptions}
                  openOnFocus={initialRender || match('ERROR') ? false : true}
                  {...prompt}
                  goToFullScreen={!match('INIT')}
                  isMobile={isMobile}
                  dispatch={dispatch}
                  descriptions={
                    credits.value <= numberOfAttempts ? (
                      <Text
                        {...props?.components?.attempts}
                        {...(credits.value === 0 && props?.components?.attemptsEmpty)}
                      >
                        {translate(
                          'cmp-flights-recommender-attempts-remaining',
                          '{remainingRequests}/10 request left for this session',
                          { remainingRequests: credits.value, totalRequest: credits.total }
                        )}
                      </Text>
                    ) : null
                  }
                  showDescriptions={isComplete || isFetchingFares}
                  renderCtaButton={renderCtaButton}
                  showCTA={showCTA}
                />
              </Col>
              {showCTA && renderCtaButton({})}
              {!isError && !noFaresToShow && (
                <Col
                  lg={12}
                  display="flex"
                  justifyContent="between"
                  style={{ clear: 'both' }}
                  {...props?.components?.infoColum}
                >
                  {/* //INFO: The status query description only show if endpoint return fares the first time */}
                  <Box display="flex">
                    <Text
                      focusReferences={statusTextRef}
                      {...props?.components?.statusTextDescription}
                    >
                      {/* className="typed-out" */}
                      {statusTextDescriptionValue()}
                    </Text>
                  </Box>
                  {likeUnlikeIcons()}
                </Col>
              )}
            </Row>
            {renderSuggestionsBox()}
            {showSecondQuery && (
              <Row {...props?.components.noMatchingCriterial?.container}>
                <Col {...props?.components.noMatchingCriterial}>
                  <Text {...props?.components?.noMatchingCriterial?.label}>
                    {translate(
                      'cmp-flights-recommendation-no-matching-criterial-label',
                      'We couldn’t find any results matching your criteria.'
                    )}
                  </Text>
                  <Text {...props?.components?.noMatchingCriterial?.description}>
                    {translate(
                      'cmp-flights-recommendation-no-matching-criterial-description',
                      'Here are some alternative options:'
                    )}
                  </Text>
                </Col>
              </Row>
            )}
            {isError && <ContainerErrorAlert {...props?.components?.alert} tryAgain={retry} />}
            <Row>
              {/* TODO: Show skeleton when make the query to the MS */}
              <Col marginTop="24px">
                {isFetchingFares &&
                  (isMobile ? (
                    <>
                      {' '}
                      {loadMoreClickAction ? (
                        renderListTwoColumns()
                      ) : (
                        <SkeletonFlightRecommenderMobile {...skeletonSettings} />
                      )}
                    </>
                  ) : (
                    <>
                      {' '}
                      {loadMoreClickAction ? (
                        renderListTwoColumns()
                      ) : (
                        <SkeletonFlightRecommender {...skeletonSettings} />
                      )}
                    </>
                  ))}
                {isComplete && (
                  <>
                    {props?.showSorting && (
                      <Row>
                        <Col>
                          <Sorting {...props?.components?.sorting} {...sorting} loading={loading} />
                        </Col>
                      </Row>
                    )}
                    {renderListTwoColumns()}
                  </>
                )}
                {(isFetchingFares || isComplete) && fares.hasMore() && (
                  <Box display="flex" justifyContent="center">
                    <LoadMorePaginator
                      {...props?.components?.pagination}
                      isLoading={isFetchingFares}
                      onClickAction={() => {
                        fares.loadMore();
                        setLoadMoreClickAction(true);
                      }}
                      focusReferences={loadMoreRef}
                    />
                  </Box>
                )}
                {(noFaresToShow || isPromptInvalid) && (
                  <NoFeaturedPopularFaresAlert
                    dispatch={dispatch}
                    {...props?.components?.alertNoResults}
                    // focusFirstElement={secondaryFiltersRef?.current?.focusFirstElement}
                    enableAccessibilityMessage={true}
                  />
                )}
              </Col>
            </Row>
            {showDisclaimer && (isComplete || isFetchingLoadMoreFares) && (
              <Row {...disclaimerContentSettings}>
                <Col xs={12}>
                  <Disclaimer
                    floatLeft
                    labelKey="cmp-low-calendar-container-disclaimer"
                    {...disclaimer}
                  />
                </Col>
                <Col
                  xs={12}
                  marginTop="10px"
                  display="flex"
                  justifyContent={isMobile ? 'start' : 'center'}
                >
                  <Disclaimer
                    floatLeft
                    {...disclaimer}
                    content={''}
                    labelKey="cmp-flight-recommendation-disclaimer-accurate"
                  />
                </Col>
              </Row>
            )}
          </>
        )}
      </Box>
    );
  }
}

FlightsRecommender.propTypes = {
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Handle the action when click on card
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getFares: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getRecommendations: PropTypes.func,
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Metadata configuration
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * isIframe
   */
  isIframe: PropTypes.bool
};
FlightsRecommender.defaultProps = {
  headerContentSettings: {},
  direction: 'ltr',
  disclaimer: {
    fontSize: '3'
  },
  disclaimerContentSettings: {
    marginTop: '2'
  },
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingBottom: '1.5rem' }
  },
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  getAirports,
  getFares,
  getRecommendations,
  integrationSettings: {},
  mainContentSettings: {},
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: {
    autoSettings: {
      market: 'us'
    },
    prompt: '',
    airport: {
      outputFields: ['iataCode', 'cityName', 'name', 'stateName', 'countryName', 'locationLabel'],
      sortingDetails: [
        {
          field: 'name',
          order: 'ASC'
        }
      ],
      setting: {
        airportSource: 'TRFX',
        routeSource: 'TRFX'
      },
      labelStructure: '{locationLabel}'
    },
    faresPerRoute: 5,
    routesLimit: 10,
    outputFormat: {
      datePattern: 'E dd MMM'
    },
    outputFields: ['flightStopCount']
  },
  showDisclaimer: true,
  showTitle: true,
  title: {},
  titleKey: 'cmp-low-calendar-container-title',
  tracking: {
    flexibleDates: true,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareFinderGenie',
    trackingConfig: {
      enable: [
        'SELECT_SUGGESTION',
        'LIKE',
        'UNLIKE',
        'SEARCH_PROMPT',
        'LOAD_MORE',
        'FARE_CLICK',
        'NO_MATCH',
        'SORTING_CHANGE',
        'CLICKED_ROUTE_DEEPLINK'
      ]
    }
  },
  localizationData: null,
  theme: null,
  meta: null,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-low-fare-calendar-sub-title',
  subTitleSettings: {},
  isIframe: false
};
FlightsRecommender.componentName = 'FlightsRecommender';

// HOC with common functionality to all containers
export default withCommonEMContainer(FlightsRecommender);
